import {
  buildPath,
  buildTemplatePath,
  Button,
  CloseButton,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { StudentBulkMerge, StudentBulkMergeResource } from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { Students } from "./Students";

const routeName = "studentBulkMerge";

export const studentBulkMergePageRoute: PageRoute = {
  handle: "Merge Students",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentBulkMergePageState = {
  selectedIds: number[];
};

export const StudentBulkMergePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);

  const { state } = useLocation();
  const [stateParam] = useState<StudentBulkMergePageState>(state);

  const [bulkMerge, setBulkMerge] = useState<StudentBulkMerge>({});
  const [resource, setResource] = useState<StudentBulkMergeResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkMerge] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    getBulkMerge(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkMerge = (studentIds: number[]) => {
    ApiService.students.getBulkMerge(studentIds).then((response) => {
      setBulkMerge(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.students.getBulkMergeResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkMerge: StudentBulkMerge) => {
    setValidationErrors({});

    ApiService.students
      .saveBulkMerge(bulkMerge)
      .then(() => {
        navigate(`../`);

        listContext.loadList?.();
        listContext.clearSelectedIds?.();
        dispatch(setAppToast(newToast("success")));
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing="sm">
            <Typography variant="inherit">{studentBulkMergePageRoute.handle}</Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkMerge)}
              disabled={!(bulkMerge.students && bulkMerge.students?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Students
        students={bulkMerge.students || []}
        resource={resource}
        mergeToStudentId={bulkMerge.mergeToStudentId}
        setMergeStudentId={(id: number) =>
          setBulkMerge(
            produce(bulkMerge, (draft) => {
              draft.mergeToStudentId = id;
            })
          )
        }
      />
    </Page>
  );
};
